/**
 * Staging 动静态奖励复测 — 收益明细风格展示（EP2RS remark + 金额 + 时间）
 */
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  Popconfirm,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from 'antd';
import { ClearOutlined, PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { api, ApiError } from '../../api/client';
import { StagingPlacementPicker } from './StagingPlacementPicker';

interface Scenario {
  id: string;
  name: string;
  description: string;
}

interface RewardRow {
  flow: string;
  beneficiary_id: number;
  beneficiary_username: string;
  beneficiary_player_number: string;
  reward_type: string;
  asset: string;
  amount: string;
  ref: string;
  note?: string;
  remark: string;
  create_time: string;
}

interface FlowResult {
  scenario_id: string;
  name: string;
  ok: boolean;
  actor: string;
  created_user?: string;
  created_user_id?: number;
  created_user_player_number?: string;
  rewards: RewardRow[];
  error?: string;
}

interface RunResult {
  run_id: string;
  base_url: string;
  created_at: string;
  flows: FlowResult[];
  all_ok: boolean;
}

interface StatusPayload {
  enabled: boolean;
  base_url: string;
  scenarios: Scenario[];
}

interface CleanupResult {
  unlinked_subs: Array<{ id: number; username: string }>;
  frozen_users: Array<{ id: number; username: string }>;
  skipped_protected: string[];
  errors: string[];
}

interface ResetAndRetestResult {
  cleanup: CleanupResult;
  test_run: RunResult;
}

function fmtDisplayAmount(amount: string, asset: string): string {
  const n = Number(amount);
  if (Number.isNaN(n)) return amount;
  if (asset.toLowerCase() === 'ep') {
    return n.toLocaleString('zh-CN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
  }
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 4 });
}

function fmtLogTime(iso: string): string {
  try {
    const d = new Date(iso);
    const pad = (x: number) => String(x).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  } catch {
    return iso;
  }
}

function RewardLogList({ rewards }: { rewards: RewardRow[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
      {rewards.map((r) => (
        <div
          key={`${r.ref}-${r.beneficiary_id}-${r.create_time}`}
          style={{
            borderRadius: 10,
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #1a2332 0%, #243044 100%)',
            color: '#e8edf5',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gridTemplateRows: 'auto auto',
            columnGap: 12,
            rowGap: 6,
            alignItems: 'start',
          }}
        >
          <Typography.Text
            style={{
              color: '#b8c5d6',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 13,
              lineHeight: 1.5,
              wordBreak: 'break-all',
            }}
          >
            {r.remark}
          </Typography.Text>
          <Typography.Text
            strong
            style={{
              color: '#ffffff',
              fontSize: 16,
              whiteSpace: 'nowrap',
              textAlign: 'right',
            }}
          >
            +{fmtDisplayAmount(r.amount, r.asset)}
          </Typography.Text>
          <div
            style={{
              gridColumn: '1 / -1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Typography.Text style={{ color: '#7a8a9e', fontSize: 12 }}>
              {fmtLogTime(r.create_time)}
            </Typography.Text>
            <Typography.Text style={{ color: '#6b9e78', fontSize: 12 }}>
              → {r.beneficiary_username}
              <span style={{ color: '#5a6878' }}> · 编号{r.beneficiary_player_number}</span>
            </Typography.Text>
          </div>
        </div>
      ))}
    </div>
  );
}

export function StagingRewardTestPage() {
  const [status, setStatus] = useState<StatusPayload | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [resetRunning, setResetRunning] = useState(false);
  const [customRunning, setCustomRunning] = useState(false);
  const [lastRun, setLastRun] = useState<RunResult | null>(null);
  const [lastCleanup, setLastCleanup] = useState<CleanupResult | null>(null);

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const data = await api.get<StatusPayload>('/api/admin/staging-reward-test/status');
      setStatus(data);
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : '加载失败');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  async function runScenario(scenarioId: string) {
    setRunningId(scenarioId);
    try {
      const data = await api.post<RunResult>(
        '/api/admin/staging-reward-test/run',
        { scenario_id: scenarioId },
        { timeout: 180_000 },
      );
      setLastRun(data);
      if (data.all_ok) {
        message.success(`复测完成 (${data.run_id})`);
      } else {
        message.warning(`部分流程失败 (${data.run_id})`);
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.code === 'ECONNABORTED') {
        message.error('请求超时（>3 分钟），请查看后端日志');
      } else {
        message.error(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setRunningId(null);
    }
  }

  async function resetAndRetest() {
    setResetRunning(true);
    try {
      const data = await api.post<ResetAndRetestResult>(
        '/api/admin/staging-reward-test/reset-and-retest',
        {},
        { timeout: 300_000 },
      );
      setLastCleanup(data.cleanup);
      setLastRun(data.test_run);
      const { unlinked_subs, frozen_users, errors } = data.cleanup;
      if (errors.length > 0) {
        message.warning(
          `清理完成（解绑 ${unlinked_subs.length} 子号、冻结 ${frozen_users.length} 账号），部分错误；复测 ${data.test_run.run_id}`,
        );
      } else if (data.test_run.all_ok) {
        message.success(
          `已清理并重跑全流程（解绑 ${unlinked_subs.length} 子号、冻结 ${frozen_users.length} 账号）`,
        );
      } else {
        message.warning(`已清理，但复测部分失败 (${data.test_run.run_id})`);
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.code === 'ECONNABORTED') {
        message.error('请求超时（>5 分钟），请查看后端日志');
      } else {
        message.error(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setResetRunning(false);
    }
  }

  async function runCustomRegister(params: {
    placement_user_id: number;
    placement_username: string;
    position: 'L' | 'R';
    sponsor_username: string;
    star_package: 'E1' | 'E2';
  }) {
    setCustomRunning(true);
    try {
      const data = await api.post<RunResult>(
        '/api/admin/staging-reward-test/run-register',
        params,
        { timeout: 180_000 },
      );
      setLastRun(data);
      if (data.all_ok) {
        message.success(`注册测试完成 (${data.run_id})`);
      } else {
        message.warning(`注册测试失败 (${data.run_id})`);
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.code === 'ECONNABORTED') {
        message.error('请求超时（>3 分钟）');
      } else {
        message.error(e instanceof ApiError ? e.message : String(e));
      }
    } finally {
      setCustomRunning(false);
    }
  }

  if (statusLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin tip="加载…" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        Staging 动静态奖励复测
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        跑注册 / 开子流程，按 App「收益明细」样式展示 EP2RS remark、金额与时间。
      </Typography.Paragraph>

      {!status?.enabled && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="当前环境未启用 Staging 复测"
          description="仅 staging 后端或设置 STAGING_REWARD_TEST_ENABLED=1 时可执行。"
        />
      )}

      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
          <Descriptions.Item label="API">{status?.base_url ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            {status?.enabled ? <Tag color="green">已启用</Tag> : <Tag>未启用</Tag>}
          </Descriptions.Item>
        </Descriptions>
        <Button icon={<ReloadOutlined />} size="small" onClick={() => void loadStatus()} style={{ marginTop: 8 }}>
          刷新状态
        </Button>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }} title="一键清理并重测">
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          解绑测试开子号（hwb001-017+、uo23456-002+ 等）、冻结 stgadm/stgfull/stgflow 等测试账号，然后自动跑「全流程 run_all」。
          不会删除账本流水；如需完全还原 prod 快照请用服务器脚本 staging-copy-prod-db.sh。
        </Typography.Paragraph>
        <Popconfirm
          title="清除 staging 测试数据并重跑全流程？"
          description="将解绑测试子号并冻结测试账号，随后执行 register + 开子 + E1 注册全流程。"
          okText="确认清理并重测"
          cancelText="取消"
          disabled={!status?.enabled || resetRunning || runningId !== null || customRunning}
          onConfirm={() => void resetAndRetest()}
        >
          <Button
            type="primary"
            danger
            icon={<ClearOutlined />}
            loading={resetRunning}
            disabled={!status?.enabled || resetRunning || runningId !== null || customRunning}
          >
            清除测试数据并重跑全流程
          </Button>
        </Popconfirm>
      </Card>

      {lastCleanup && (
        <Alert
          type={lastCleanup.errors.length > 0 ? 'warning' : 'info'}
          showIcon
          style={{ marginBottom: 16 }}
          message="最近一次清理摘要"
          description={
            <Space direction="vertical" size={4}>
              <span>
                解绑子号 {lastCleanup.unlinked_subs.length} 个
                {lastCleanup.unlinked_subs.length > 0 &&
                  `（${lastCleanup.unlinked_subs.map((s) => s.username).join(', ')}）`}
              </span>
              <span>
                冻结账号 {lastCleanup.frozen_users.length} 个
                {lastCleanup.frozen_users.length > 0 &&
                  `（${lastCleanup.frozen_users.map((u) => u.username).join(', ')}）`}
              </span>
              {lastCleanup.errors.length > 0 && (
                <Typography.Text type="danger">{lastCleanup.errors.join('；')}</Typography.Text>
              )}
            </Space>
          }
        />
      )}

      <Typography.Title level={5}>自定义安置测试</Typography.Title>
      <Card size="small" style={{ marginBottom: 24 }}>
        <StagingPlacementPicker
          disabled={!status?.enabled || resetRunning || customRunning}
          running={customRunning}
          onRegister={(p) => void runCustomRegister(p)}
        />
      </Card>

      <Typography.Title level={5}>预设场景</Typography.Title>
      <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 24 }}>
        {(status?.scenarios ?? []).map((s) => (
          <Card key={s.id} size="small" title={s.name}>
            <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {s.description}
            </Typography.Paragraph>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              loading={runningId === s.id}
              disabled={!status?.enabled || resetRunning || (runningId !== null && runningId !== s.id) || customRunning}
              onClick={() => void runScenario(s.id)}
            >
              运行
            </Button>
          </Card>
        ))}
      </Space>

      {lastRun && (
        <>
          <Typography.Title level={5}>
            结果 · {lastRun.run_id}{' '}
            {lastRun.all_ok ? <Tag color="success">全部通过</Tag> : <Tag color="error">有失败</Tag>}
          </Typography.Title>

          <Collapse
            defaultActiveKey={lastRun.flows.map((f) => f.scenario_id)}
            items={lastRun.flows.map((f) => ({
              key: f.scenario_id,
              label: (
                <Space>
                  {f.ok ? <Tag color="success">OK</Tag> : <Tag color="error">FAIL</Tag>}
                  <span>{f.name}</span>
                  {f.rewards.length > 0 && <Tag>{f.rewards.length} 笔</Tag>}
                </Space>
              ),
              children: (
                <>
                  <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 8 }}>
                    <Descriptions.Item label="操作账号">{f.actor}</Descriptions.Item>
                    <Descriptions.Item label="新建用户">
                      {f.created_user
                        ? `${f.created_user}${f.created_user_player_number ? ` · 编号${f.created_user_player_number}` : ''}`
                        : '—'}
                    </Descriptions.Item>
                    {f.error && (
                      <Descriptions.Item label="错误" span={2}>
                        <Typography.Text type="danger">{f.error}</Typography.Text>
                      </Descriptions.Item>
                    )}
                  </Descriptions>

                  {f.rewards.length === 0 ? (
                    <Alert type="info" message="本次流程无动静态奖励入账" />
                  ) : (
                    <RewardLogList rewards={f.rewards} />
                  )}
                </>
              ),
            }))}
          />
        </>
      )}
    </div>
  );
}
